import { Component, EventEmitter, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { CurrencyPipe } from '@angular/common';

import { ConfirmationService, MessageService } from 'primeng/api';
import { CellConfig, jsPDF, TableConfig, TextOptionsLight } from 'jspdf';

import { PDFDocument } from 'pdf-lib';

import { AuthManagerService } from '../../../services/auth/auth-manager.service';
import { CaliforniaCompliancePageComponent } from '../compliance-pages/usa-pages/california-compliance-page/california-compliance-page.component';
import { DefaultCompliancePageComponent } from '../compliance-pages/usa-pages/default-compliance-page/default-compliance-page.component';
import { DeviceManagerService } from '../../../core/services/device-manager.service';
import { FormUtils } from '../../../shared-ui/utils/form-utils';
import { IllinoisCompliancePageComponent } from '../compliance-pages/usa-pages/illinois-compliance-page/illinois-compliance-page.component';
import { ImageApiService } from '../../../shared-api/images/image-api.service';
import { Location } from '../../../services/location/location.model';
import { LocationManagerService } from '../../../services/location/location-manager.service';
import { PdfSectionOutputResult } from '../../pdf-utils/pdf-section-output-result';
import { PricingType } from '../../../services/miscellaneous-part/miscellaneous-part.model';
import { PurchaseOrder } from '../../../services/purchase-order/purchase-order.model';
import { PurchaseOrderLineItem, PurchaseOrderLineItemMiscellaneous } from '../../../services/purchase-order/purchase-order-line-item.model';
import { PurchaseOrderManagerService } from '../../../services/purchase-order/purchase-order-manager.service';
import { PurchaseOrderTransactionPartType } from '../../../services/purchase-order/purchase-order-transaction';
import { TexasCompliancePageComponent } from '../compliance-pages/usa-pages/texas-compliance-page/texas-compliance-page.component';
import { Utils } from '../../../services/utils.service';
import { WestVirginiaCompliancePageComponent } from '../compliance-pages/usa-pages/west-virginia-compliance-page/west-virginia-compliance-page.component';
import { finalize } from 'rxjs/operators';
import { CurrencyConvertPipe } from 'src/app/services/custom-currency.pipe';
import { bottom } from '@popperjs/core';
import { environment } from 'src/environments/environment';
import { UkCompliancePageComponent } from '../compliance-pages/eu-pages/uk-compliance-page/uk-compliance-page.component';
import { LocalCacheManager } from 'src/app/managers/local-cache.manager';
import { EnvUtils } from 'src/app/shared-ui/utils/environment-utils';
import { MissisipiCompliancePageComponent } from '../compliance-pages/usa-pages/missisipi-compliance-page/missisipi-compliance-page.component';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { GeolocationService } from 'src/app/services/geolocation/geolocation.service';
import { openBlobUrl } from 'src/app/common/guards/blob-url-guard';

export enum SignaturePageType
{
    Default,
    California,
    Illinois,
    Missisipi,
    Texas,
    WestVirginia,
    Uk,
}

@Component({
    selector: 'ls-purchase-order-pdf',
    templateUrl: './purchase-order-pdf.component.html',
    styleUrls: [ './purchase-order-pdf.component.less' ]
})
export class PurchaseOrderPDFComponent implements OnInit
{
    @ViewChild('caCompliancePage', { static: false }) caCompliancePage: CaliforniaCompliancePageComponent;
    @ViewChild('ilCompliancePage', { static: false }) ilCompliancePage: IllinoisCompliancePageComponent;
    @ViewChild('msCompliancePage', { static: false }) msCompliancePage: MissisipiCompliancePageComponent;
    @ViewChild('txCompliancePage', { static: false }) txCompliancePage: TexasCompliancePageComponent;
    @ViewChild('wvCompliancePage', { static: false }) wvCompliancePage: WestVirginiaCompliancePageComponent;
    @ViewChild('defaultCompliancePage', { static: false }) defaultCompliancePage: DefaultCompliancePageComponent;
    @ViewChild('ukCompliancePage', { static: false }) ukCompliancePage: UkCompliancePageComponent;

    @Output() signatureCompleted = new EventEmitter<boolean>();
    @Output() generationCompleted = new EventEmitter<boolean>();

    isEuLaunch: boolean = EnvUtils.isEuRegion;

    po: PurchaseOrder;
    pdf: jsPDF;
    needsSignature = false;
    result: PdfSectionOutputResult;

    compliancePages: {[key: number]: any} = {
        0: DefaultCompliancePageComponent,
        1: CaliforniaCompliancePageComponent,   
        2: IllinoisCompliancePageComponent,
        3: MissisipiCompliancePageComponent,
        4: TexasCompliancePageComponent,
        5: WestVirginiaCompliancePageComponent,
        6: UkCompliancePageComponent
    }
    pdfDimensions = {
        general: {
            marginLeft: 25,
            marginTop: 20,
            regularFontSize: 11,
            tableHeaderSize: 15,
            logoWidth: 162,
            logoHeight: 30,
            assayVendorLogoHeight: 50,
            bodyWidth: 555,
            pageHeight: 800,
            textPadding: 3,
            widthPerCharacter: 3.6,

            header: {
                fullRowHeight: 65,
                rowSpacing: 30,
                centerMargin: 20,
                headerRightColumnWidth: 100,
                singleInfoWidth: 100,
            },

            colors: {
                darkGray: '#AAA',
                lightGray: '#CCC',
                white: 'white',
                tableHeaderColor: '#AAA',
                alternateRowColor: '#CCC',
            }
        },
    };

    private signaturePdf: any;
    private pdfBrowserWindow: Window;
    private location: Location;
    private _signaturePageType: SignaturePageType
    private _selectedContactId?: number;
    get selectedContactId(): number {
        return this._selectedContactId;
    }

    protected get signaturePageType(): SignaturePageType
    {
        return this._signaturePageType;
    }
    protected set signaturePageType(value: SignaturePageType) 
    {
        if(value == SignaturePageType.Uk)
            this.ukCompliancePage?.initSignature();
        this._signaturePageType = value
    }
    private showDetails: boolean;

    SignaturePageType = SignaturePageType;



    constructor(private deviceManager: DeviceManagerService,
                public authManager: AuthManagerService,
                private poManager: PurchaseOrderManagerService,
                private locationManager: LocationManagerService,
                private messageService: MessageService,
                private currencyPipe: CurrencyConvertPipe,
                private viewContainerRef: ViewContainerRef,
                private geolocationService: GeolocationService) { }

    ngOnInit(): void
    {
        this.locationManager.getCurrentLocation().subscribe(location =>
        {
            this.location = location;
        });
    }

    initializeBrowserWindow(): void
    {
        this.pdfBrowserWindow = this.deviceManager.isiOS ? window.open() : null;
    }
    async create(emailToVendor: boolean, showDetails: boolean): Promise<void>
    {
        this.po = this.poManager.current;

        if(this.po.newVersion == true && !this.isEuLaunch) {
            this.createDynamically(emailToVendor, showDetails);
            return;
        }

        this.pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'px',
            format: 'letter'
        });
        this.pdfDimensions.general.bodyWidth = this.pdf.internal.pageSize.width - this.pdfDimensions.general.marginLeft * 2;
        this.pdfDimensions.general.pageHeight = this.pdf.internal.pageSize.height;
        
        this.result = new PdfSectionOutputResult();
        this.showDetails = showDetails;
        const pdfPages = [ ];
        
        if(this.isEuLaunch){
            this.pdfDimensions.general.logoWidth = 70
            this.pdfDimensions.general.logoHeight = 70
            
            if(this.po.location == null || this.authManager.currentUser.isAssayUser || this.authManager.currentUser.exemptFromVendorLicenseRequirements){
                this.createHeader();
                const willOutputConverters = this.po.lineItemsAllConverters.length > 0;
                const willOutputMisc = this.po.lineItemsMiscellaneous.length > 0;
                this.createConvertersTable();
                if (willOutputConverters && willOutputMisc) this.addSpaceBetweenTables();
                this.createMiscTable();
                pdfPages.push(this.pdf);
            }
        }
        else {
            this.outputPageFooter();
            this.createHeader();
            const willOutputConverters = this.po.lineItemsAllConverters.length > 0;
            const willOutputMisc = this.po.lineItemsMiscellaneous.length > 0;
            this.createConvertersTable();
            if (willOutputConverters && willOutputMisc) this.addSpaceBetweenTables();
            this.createMiscTable();
            this.createNotes();
            pdfPages.push(this.pdf);
        }
        
        if ((this.po.signaturePageUrl == null && this.po.location != null &&
            !this.authManager.currentUser.isAssayUser && !this.authManager.currentUser.exemptFromVendorLicenseRequirements) && (this.po.signaturePageUrl == null && !this.po.isLocked))
        {
            // Save the existing PDF and generate just the signature page
            const savedResult = this.result;
            const savedPdf = this.pdf;
            this.result = new PdfSectionOutputResult();

            const signaturePdf = new jsPDF({
                orientation: 'portrait',
                unit: 'px',
                format: 'letter'
            });
            this.pdf = signaturePdf;
            switch (this.signaturePageType)
            {
                case SignaturePageType.Uk:
                    this.ukCompliancePage.generateSignaturePage(this);
                    this._selectedContactId = this.ukCompliancePage.selectedContact?.id;
                    break;
            
                case SignaturePageType.California:
                    this.caCompliancePage.generateSignaturePage(this);
                    this._selectedContactId = this.caCompliancePage.sellerContact?.id;
                    break;

                case SignaturePageType.Illinois:
                    this.ilCompliancePage.generateSignaturePage(this);
                    this._selectedContactId = this.ilCompliancePage.selectedContact?.id;
                    break;

                case SignaturePageType.Missisipi:
                    this.msCompliancePage.generateSignaturePage(this);
                    this._selectedContactId = this.msCompliancePage.selectedContact?.id;
                    break;

                case SignaturePageType.Texas:
                    this.txCompliancePage.generateSignaturePage(this);
                    break;

                case SignaturePageType.WestVirginia:
                    this.wvCompliancePage.generateSignaturePage(this);
                    break;

                case SignaturePageType.Default:
                default:
                    this.defaultCompliancePage.generateSignaturePage(this);
                    this._selectedContactId = this.defaultCompliancePage.selectedContact?.id;
                    break;
            }
            
            const blob = signaturePdf.output('blob');
            const file = new File([ blob ], `po_${this.po.id}_signature.pdf`);
            const timestamp = new Date().getTime();

            try {
                let signatureResult = await lastValueFrom(this.poManager.uploadSignaturePageAndLock(this.po.id, file, this._selectedContactId, this.signaturePageType, this.signaturePageType == SignaturePageType.WestVirginia ? this.wvCompliancePage.additionalData : null))
                if (!signatureResult.success)
                {
                    this._selectedContactId = null;
                    this.messageService.add({ severity: 'error', summary: 'Upload Signature Page Error', detail: 'There was an unknown error upload signature page' });
                    return;
                }

                this.po.signaturePageUrl = `${signatureResult.url}?t=${timestamp}`;
                pdfPages.push(this.po.signaturePageUrl);

                // Generate extra pages if necessary
                let hasExtraPages = false;
                let extraPagesPdf: jsPDF;
                if (this.signaturePageType === SignaturePageType.California)
                {
                    this.result = new PdfSectionOutputResult();
                    extraPagesPdf = new jsPDF({
                        orientation: 'portrait',
                        unit: 'px',
                        format: 'letter'
                    });
                    this.pdf = extraPagesPdf;
                    hasExtraPages = this.caCompliancePage.generateExtraPages(this);
                }

                if(this.signaturePageType == SignaturePageType.Uk){
                    await this.openOrEmailPdf(pdfPages, emailToVendor);
                    return;
                }
                if (!hasExtraPages && this.po.extraPagesUrl == null)
                {
                    await this.openOrEmailPdf(pdfPages, emailToVendor);
                    return;
                }

                const extraPagesBlob = extraPagesPdf.output('blob');
                const extraPagesFile = new File([ extraPagesBlob ], `po_${this.po.id}_extra_pages.pdf`);
                const extraPagesCall = hasExtraPages ? this.poManager.uploadExtraPages(this.po.id, extraPagesFile)
                    : this.poManager.clearExtraPages(this.po.id);
                let extraResult = await lastValueFrom(extraPagesCall);
                if (!extraResult.success)
                {
                    this.messageService.add({ severity: 'error', summary: 'Upload Extra Pages Error', detail: 'There was an unknown error uploading extra pages.' });
                    this.poManager.unlock().subscribe();
                    this._selectedContactId = null;
                    return;
                }
                if (extraResult.url !== undefined)
                {
                    this.po.extraPagesUrl = `${extraResult.url}?t=${timestamp}`;
                    pdfPages.push(this.po.extraPagesUrl);
                }
                else
                {
                    this.po.extraPagesUrl = undefined;
                }
                this.result = savedResult;
                this.pdf = savedPdf;
                this.poManager.currentPOUpdated();
                await this.openOrEmailPdf(pdfPages, emailToVendor);
            } catch(error) {
                this.messageService.add({ severity: 'error', summary: 'Upload Signature Page Error', detail: 'Unknown error occurred.' });
                this._selectedContactId = null;
            }

            return
        }
        else
        {
            // We have responsibility to lock the PO and since that is not done with the signature block above (since we are not
            // creating signature pages), we need to lock it here
            if (!this.poManager.current.isLocked){
                this.poManager.lock(this.signaturePageType ?? SignaturePageType.Default, this.selectedContactId, this.signaturePageType == SignaturePageType.WestVirginia ? this.wvCompliancePage.additionalData : null).subscribe(() => this.generationCompleted.emit(true));
            }
        }

        if (this.po.signaturePageUrl != null) pdfPages.push(this.po.signaturePageUrl);
        if (this.po.extraPagesUrl != null && !this.isEuLaunch) pdfPages.push(this.po.extraPagesUrl);
        await this.openOrEmailPdf(pdfPages, emailToVendor);
    }
    async createDynamically(emailToVendor: boolean, showDetails: boolean): Promise<void>
    {
        this.po = this.poManager.current;

        this.pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'px',
            format: 'letter'
        });
        this.pdfDimensions.general.bodyWidth = this.pdf.internal.pageSize.width - this.pdfDimensions.general.marginLeft * 2;
        this.pdfDimensions.general.pageHeight = this.pdf.internal.pageSize.height;
        
        this.result = new PdfSectionOutputResult();
        this.showDetails = showDetails;
        const pdfPages = [ ];
        
        if(this.isEuLaunch){
            this.pdfDimensions.general.logoWidth = 70
            this.pdfDimensions.general.logoHeight = 70
            
            if(this.po.location == null || this.authManager.currentUser.isAssayUser || this.authManager.currentUser.exemptFromVendorLicenseRequirements){
                this.createHeader();
                const willOutputConverters = this.po.lineItemsAllConverters.length > 0;
                const willOutputMisc = this.po.lineItemsMiscellaneous.length > 0;
                this.createConvertersTable();
                if (willOutputConverters && willOutputMisc) this.addSpaceBetweenTables();
                this.createMiscTable();
                pdfPages.push(this.pdf);
            }
        }
        else {
            this.outputPageFooter();
            this.createHeader();
            const willOutputConverters = this.po.lineItemsAllConverters.length > 0;
            const willOutputMisc = this.po.lineItemsMiscellaneous.length > 0;
            this.createConvertersTable();
            if (willOutputConverters && willOutputMisc) this.addSpaceBetweenTables();
            this.createMiscTable();
            this.createNotes();
            pdfPages.push(this.pdf);
        }
        

        if(this.po.isLocked && this.po.signaturePageType == null){
            await this.openOrEmailPdf(pdfPages, emailToVendor);
            return;
        }
        
        let compliancePageRef = null;
        let componentRef: any = null;
        let pageType = this.po.isLocked ? this.po.signaturePageType : this.signaturePageType;
        
        if(this.po.location == null) {
            if(!this.po.isLocked){
                let location = await this.geolocationService.getCurrentLocation();
                if(location == null || (this.po.latitude != null && this.po.longitude != null)){
                    await firstValueFrom(this.poManager.lock(SignaturePageType.Default, null, null));
                } else {
                    await firstValueFrom(this.poManager.lock(SignaturePageType.Default, null, null, location.latitude, location.longitude));
                }
                this.generationCompleted.emit(true);
            }
            await this.openOrEmailPdf(pdfPages, emailToVendor);
            return;
        }
        this.result = new PdfSectionOutputResult();

        const signaturePdf = new jsPDF({
            orientation: 'portrait',
            unit: 'px',
            format: 'letter'
        });
        this.pdf = signaturePdf;

        if(this.po.isLocked){
            if(this.po.signaturePageType != null) {
                
                let compliancePage = this.compliancePages[this.po.signaturePageType];
                componentRef = this.viewContainerRef.createComponent(compliancePage);
                compliancePageRef = componentRef.instance;
            }
        } else if(this.signaturePageType != null) {
            if(this.signaturePageType == SignaturePageType.California)
                compliancePageRef = this.caCompliancePage;
            else if(this.signaturePageType == SignaturePageType.Illinois)
                compliancePageRef = this.ilCompliancePage;
            else if(this.signaturePageType == SignaturePageType.Missisipi)
                compliancePageRef = this.msCompliancePage;
            else if(this.signaturePageType == SignaturePageType.Texas)
                compliancePageRef = this.txCompliancePage;
            else if(this.signaturePageType == SignaturePageType.WestVirginia)
                compliancePageRef = this.wvCompliancePage;
            else
                compliancePageRef = this.defaultCompliancePage;
        }

        if(compliancePageRef != null) {
            await compliancePageRef.generateSignaturePage(this);
            if(pageType == SignaturePageType.California)
                this._selectedContactId = compliancePageRef['sellerContact']?.id;
            else
                this._selectedContactId = compliancePageRef['selectedContact']?.id;
            pdfPages.push(this.pdf);
            
            if (pageType === SignaturePageType.California)
            {
                this.result = new PdfSectionOutputResult();
                let extraPagesPdf = new jsPDF({
                    orientation: 'portrait',
                    unit: 'px',
                    format: 'letter'
                });
                this.pdf = extraPagesPdf;
                if(await compliancePageRef.generateExtraPages(this))
                    pdfPages.push(this.pdf);
            }
        }
        if(componentRef != null)
        {
            componentRef.destroy();
        } 
        else 
        {
            try {
                let signatures = null;
                if(!this.authManager.currentUser.exemptFromVendorLicenseRequirements && this.po.location != null)
                    signatures = await firstValueFrom(this.poManager.setSignatures(compliancePageRef.sellerSignatureFile, compliancePageRef.purchaserSignatureFile))
                
                try {
                    let location = await this.geolocationService.getCurrentLocation();
                    if(this.authManager.currentUser.exemptFromVendorLicenseRequirements){
                        if(location == null || (this.po.latitude != null && this.po.longitude != null)){
                            await firstValueFrom(this.poManager.lock(SignaturePageType.Default, null, null));
                        } else {
                            await firstValueFrom(this.poManager.lock(SignaturePageType.Default, null, null, location.latitude, location.longitude));
                        }
                    } else {
                        if(location == null || (this.po.latitude != null && this.po.longitude != null)){
                            await firstValueFrom(this.poManager.uploadSignaturePageAndLockV2(this.po.id, this._selectedContactId, pageType, signatures.sellerUrl, signatures.purchaserUrl));
                        } else {
                            await firstValueFrom(this.poManager.uploadSignaturePageAndLockV2(this.po.id, this._selectedContactId, pageType, signatures.sellerUrl, signatures.purchaserUrl, location.latitude, location.longitude));
                        }
                    }
                } catch (lockEx) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Cannot close Purchase Order. Please try again later' });
                    return;
                }
            } catch(ex) {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Cannot save Signature(s). Please try again later' });
                return;
            }
        }
            
        componentRef = null;
        compliancePageRef = null;
        await this.openOrEmailPdf(pdfPages, emailToVendor);
    }

    blobTmpUrl?: string;
    downloadPo(){
        const link = document.createElement('a');
        link.href = this.blobTmpUrl;
        link.download = 'purchaseOrder.pdf';
        link.click();
    }
    private async openOrEmailPdf(pdfPages: jsPDF[], emailToVendor: boolean): Promise<void>
    {
        const blob = await PurchaseOrderPDFComponent.combinePDFs(pdfPages);
        if (!emailToVendor)
        {
            const blobUrl = URL.createObjectURL(blob);
            this.generationCompleted.emit(true);
            let newWindow = openBlobUrl(blobUrl, false);
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
               this.blobTmpUrl = blobUrl;
            }
            return;
        }
        this.pdfBrowserWindow = null;

        this.poManager.emailPdf(blob).subscribe(() =>
            {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'PDF has been emailed.' });
                this.generationCompleted.emit(true);
            },
            () =>
            {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to email PDF.' });
                this.generationCompleted.emit(false);
            });

    }
    // tslint:disable-next-line:member-ordering
    static async combinePDFs(pdfs: any[]): Promise<Blob>
    {
        const pdfDoc = await PDFDocument.create();
        const buffers: ArrayBuffer[] = [];
        // tslint:disable-next-line:prefer-for-of
        for (let iPdf = 0; iPdf < pdfs.length; iPdf++)
        {
            if (typeof pdfs[iPdf] === 'string')
                buffers.push(await fetch(pdfs[iPdf]).then(res => res.arrayBuffer()));
            else
                buffers.push(pdfs[iPdf].output('arraybuffer'));
        }

        for (const buffer of buffers)
        {
            const subDoc = await PDFDocument.load(buffer);
            const pageIndices = subDoc.getPageIndices();
            for (const index of pageIndices)
            {
                const [pages] = await pdfDoc.copyPages(subDoc, [index]);
                pdfDoc.addPage(pages);
            }
        }
        let bytes = await pdfDoc.save();
        let blob = new Blob([bytes.buffer as unknown as BlobPart], { type: 'application/pdf' });
        return blob;
    }

    //region Header Section
    private createHeader(): void
    {
        let purchaseOrderDate = FormUtils.formatDateTimeForDisplay(this.po.purchaseDate);

        const general = this.pdfDimensions.general;
        const header = general.header;
        const colors = general.colors;

        this.pdf.setFontSize(general.regularFontSize);

        if(this.poManager.current.batchNumber != undefined && this.poManager.current.batchNumber != '')
            this.pdf.text(this.poManager.current.batchNumber, general.marginLeft + general.bodyWidth - this.textWidth(this.poManager.current.batchNumber) - general.textPadding, general.marginTop - 5);

        // Buyer
        let sectionBounds = {
            left: general.marginLeft, top: general.marginTop, right: this.center - header.centerMargin / 2,
            bottom: general.marginTop + 4 * this.lineHeight
        };
        if (this.po.location != null || this.po.vendor.parentVendor != null)
        {
            this.pdf.text('Buyer:', sectionBounds.left, sectionBounds.top - 5);
            this.drawSectionArea(sectionBounds, colors.white);
            const buyerName = this.po.buyerName;

            this.addLineToSection(buyerName, sectionBounds, 1);
            if (this.po.location != null)
            {
                this.addLineToSection(this.location.subsidiary.displayName + ' - ' +
                    (this.authManager.currentUser.purchaseOrderLocationName || this.po.location.name), sectionBounds, 2);
                this.addLineToSection(this.authManager.currentUser.purchaseOrderAddress || this.po.location.address, sectionBounds, 3);
                this.addLineToSection(this.authManager.currentUser.purchaseOrderCityStateZip || this.po.location.cityStateZip,
                    sectionBounds, 4);
            }
            else
            {
                const parentVendor = this.po.vendor.parentVendor;
                this.addLineToSection(parentVendor.name, sectionBounds, 2);
                this.addLineToSection(parentVendor.address, sectionBounds, 3);
                this.addLineToSection(`${parentVendor.cityStateZip}`, sectionBounds, 4);
            }
        }

        // Seller
        sectionBounds.top = sectionBounds.bottom + header.rowSpacing + this.lineHeight;
        sectionBounds.bottom = sectionBounds.top + 3 * this.lineHeight;
        this.pdf.text('Seller:', sectionBounds.left, sectionBounds.top - 5);
        this.drawSectionArea(sectionBounds, colors.white);
        this.addLineToSection(this.po.vendorName, sectionBounds, 1);
        if (this.po.vendor.address != null) this.addLineToSection(this.po.vendor.address, sectionBounds, 2);
        this.addLineToSection(this.po.vendor.cityStateZip, sectionBounds, 3);

        // PO Number and Date
        sectionBounds.bottom = sectionBounds.top + this.lineHeight;
        sectionBounds.left = this.center + general.bodyWidth / 2 - header.singleInfoWidth;
        sectionBounds.right = this.center + general.bodyWidth / 2;
        this.pdf.text('PO #:', sectionBounds.left - 35, sectionBounds.top + 15);
        this.drawSectionArea(sectionBounds, colors.white);
        this.addLineToSection(this.po.number, sectionBounds, 1);
        sectionBounds.top = sectionBounds.bottom + 12;
        sectionBounds.bottom = sectionBounds.top + this.lineHeight;
        this.pdf.text('Date:', sectionBounds.left - 35, sectionBounds.top + 15);
        this.drawSectionArea(sectionBounds, colors.white);
        this.addLineToSection(purchaseOrderDate, sectionBounds, 1);

        // Total Price
        let totalTop = sectionBounds.bottom + 20;
        let totalPriceText = `Total: ${this.currencyPipe.transform(this.po.totalPrice, this.po.currency, true)}`;
        
        this.pdf.text(totalPriceText, general.marginLeft + general.bodyWidth - this.textWidth(totalPriceText) - general.textPadding, totalTop);
        
        totalTop += 11;
        let adjustment = `Adjustment: ${this.currencyPipe.transformWithoutRounding((this.po.catsAdjustment ?? 0) + (this.po.nonFerrousAdjustment ?? 0), this.po.currency, true)}`
        this.pdf.text(adjustment, general.marginLeft + general.bodyWidth - this.textWidth(adjustment) - general.textPadding, totalTop);
        
        let orgFont = this.pdf.getFont();
        this.pdf.setFont(orgFont.fontName, orgFont.fontStyle, 'bold')

        totalTop += 11;
        let adjustedTotal = `Adjusted total: ${this.currencyPipe.transform(this.po.totalPrice + (this.po.catsAdjustment ?? 0) + (this.po.nonFerrousAdjustment ?? 0), this.po.currency, true)}`;
        this.pdf.text(adjustedTotal, general.marginLeft + general.bodyWidth - this.textWidth(adjustedTotal) - general.textPadding, totalTop);
        
        this.pdf.setFont(orgFont.fontName, orgFont.fontStyle, 'normal')
        this.result.bottomOfOutput = totalTop + 10;

        // Logo
        if (this.authManager.currentUser.isAssayUser)
        {
            const logoImageUrl = this.po.vendor.logoImageUrl != null ? this.po.vendor.logoImageUrl
                : this.po.vendor.parentVendor != null ? this.po.vendor.parentVendor.logoImageUrl : null;
            if (logoImageUrl != null)
            {
                const imageProperties = this.pdf.getImageProperties(logoImageUrl);
                const sizeRatio = imageProperties.width / imageProperties.height;
                const logoHeight = general.assayVendorLogoHeight;
                const logoWidth = logoHeight * sizeRatio;
                sectionBounds = { left: this.center + general.bodyWidth / 2 - logoWidth - 10, top: general.marginTop + 2,
                    right: 0, bottom: 0 };
                this.pdf.addImage(logoImageUrl, 'PNG', sectionBounds.left, sectionBounds.top + 5, logoWidth, logoHeight);
            }
        }
        else
        {
            sectionBounds = { left: this.center + general.bodyWidth / 2 - general.logoWidth - 10, top: general.marginTop + 12,
                right: 0, bottom: 0 };
            this.pdf.addImage(this.location.subsidiary.name !== 'Legend' ? 'assets/images/elemental/logo_horizontal.png?q=2' : 'assets/images/logo_simple.png?q=2', 'PNG',
                sectionBounds.left, sectionBounds.top + 5, general.logoWidth, general.logoHeight);
        }
    }

    private drawSectionArea(sectionBounds, backgroundColor: string): void
    {
        this.pdf.setDrawColor(0, 0, 0);
        this.pdf.setFillColor(backgroundColor);
        this.pdf.roundedRect(sectionBounds.left, sectionBounds.top,
            sectionBounds.right - sectionBounds.left, sectionBounds.bottom - sectionBounds.top + 8, 5, 5, 'DF');
    }

    private addLineToSection(text: string, sectionBounds: any, lineNumber: number): void
    {
        this.pdf.text(text, sectionBounds.left + this.pdfDimensions.general.textPadding,
            sectionBounds.top + this.lineHeight * lineNumber);
    }
    //endregion

    //region Converters, Custom, No-Numbers, and Categories tables
    private createConvertersTable(): boolean
    {
        if (this.po.lineItemsAllConverters.length === 0) return false;

        this.checkForBottomOfPage(this.lineHeight * 1.5 + this.tableLineHeight * 2);
        this.createTableTitle(PurchaseOrderTransactionPartType.Converter);
        if (this.showDetails)
        {
            this.outputLineItems(this.po.lineItemsAllConverters, PurchaseOrderTransactionPartType.Converter);
            return;
        }

        // Display converter group lines
        const grouped = Utils.group(this.po.lineItemsAllConverters, li => li.converterGroupName);
        const lines = grouped.map(group => ({
            type: PurchaseOrderTransactionPartType.Converter,
            converterGroup: group.key,
            quantity: group.key.toLocaleLowerCase() == 'loose hc' ? 0 : Utils.sum(group.data, 'quantity'),
            totalPrice: Utils.sum(group.data, 'totalPrice'),
            levelPercentage: group.key.toLocaleLowerCase() == 'loose hc' ? Utils.sum(group.data, 'levelPercentage') : 0,
        }));
        this.createGroupLineItems(lines, PurchaseOrderTransactionPartType.Converter);
        return true;
    }

    private createCustomTable(): boolean
    {
        if (this.po.lineItemsCustom.length === 0) return false;

        this.checkForBottomOfPage(this.lineHeight * 1.5 + this.tableLineHeight * 2);
        this.createTableTitle(PurchaseOrderTransactionPartType.Custom);
        if (this.showDetails)
        {
            this.outputLineItems(this.po.lineItemsCustom, PurchaseOrderTransactionPartType.Custom);
            return;
        }

        // Display converter group lines
        const grouped = Utils.group(this.po.lineItemsCustom, li => li.converterGroupName);
        const lines = grouped.map(group => ({
            converterGroup: group.key,
            quantity: Utils.sum(group.data, 'quantity'),
            totalPrice: Utils.sum(group.data, 'totalPrice'),
        }));
        this.createGroupLineItems(lines, PurchaseOrderTransactionPartType.Custom);
        return true;
    }

    private createCategoryTable(): boolean
    {
        if (this.po.lineItemsCategory.length === 0) return false;

        this.checkForBottomOfPage(this.lineHeight * 1.5 + this.tableLineHeight * 2);
        this.createTableTitle(PurchaseOrderTransactionPartType.Category);
        if (this.showDetails)
        {
            this.outputLineItems(this.po.lineItemsCategory, PurchaseOrderTransactionPartType.Category);
            return;
        }

        // Display converter group lines
        const grouped = Utils.group(this.po.lineItemsCategory, li => li.converterGroupName);
        const lines = grouped.map(group => ({
            converterGroup: group.key,
            quantity: Utils.sum(group.data, 'quantity'),
            totalPrice: Utils.sum(group.data, 'totalPrice'),
        }));
        this.createGroupLineItems(lines, PurchaseOrderTransactionPartType.Category);
        return true;
    }

    private createNoNumberTable(): boolean
    {
        if (this.po.lineItemsNoNumber.length === 0) return false;

        this.checkForBottomOfPage(this.lineHeight * 1.5 + this.tableLineHeight * 2);
        this.createTableTitle(PurchaseOrderTransactionPartType.NoNumber);
        if (this.showDetails)
        {
            this.outputLineItems(this.po.lineItemsNoNumber, PurchaseOrderTransactionPartType.NoNumber);
            return;
        }

        // Display converter group lines
        const grouped = Utils.group(this.po.lineItemsNoNumber, li => li.converterGroupName);
        const lines = grouped.map(group => ({
            converterGroup: group.key,
            quantity: Utils.sum(group.data, 'quantity'),
            totalPrice: Utils.sum(group.data, 'totalPrice'),
        }));
        this.createGroupLineItems(lines, PurchaseOrderTransactionPartType.NoNumber);
        return true;
    }

    private createMiscTable(): boolean
    {
        if (this.po.lineItemsMiscellaneous.length === 0) return false;

        this.checkForBottomOfPage(this.lineHeight * 1.5 + this.tableLineHeight * 2);
        this.createTableTitle(PurchaseOrderTransactionPartType.Miscellaneous);
        this.outputLineItems(this.po.lineItemsMiscellaneous, PurchaseOrderTransactionPartType.Miscellaneous);
        return true;
    }

    private createNotes(): boolean 
    {
        if(this.po.notes == null)
            return false;

        this.result.bottomOfOutput += (this.lineHeight * 2) + 4;

        let titleWidth = this.pdf.getTextWidth("Notes:");
        this.checkForBottomOfPage(this.pdf.getTextDimensions(this.po.notes, {maxWidth: this.pdfDimensions.general.bodyWidth}).h - titleWidth - 4)
        this.pdf.text('Notes:', this.pdfDimensions.general.marginLeft, this.result.bottomOfOutput, {maxWidth: this.pdfDimensions.general.bodyWidth})
        this.pdf.text(this.po.notes, this.pdfDimensions.general.marginLeft + titleWidth + 4, this.result.bottomOfOutput, {maxWidth: this.pdfDimensions.general.bodyWidth - titleWidth - 4})
        return true;
    }

    private outputLineItems(lineItems: PurchaseOrderLineItem[] | PurchaseOrderLineItemMiscellaneous[],
                            type: PurchaseOrderTransactionPartType): void
    {
        const columns = type === PurchaseOrderTransactionPartType.Miscellaneous ? [
                { title: 'Type', widthPercent: 25, align: 'left',
                    calcFunc: (item) => item.pricingType === PricingType.ByPiece ? 'By Piece' : 'By Pound' },
                { title: 'Description', widthPercent: 35, align: 'left', propertyName: 'description' },
                { title: 'Quantity', widthPercent: 10, align: 'center', propertyName: 'quantity' },
                { title: 'Price Per Item', widthPercent: 15, align: 'right',
                    calcFunc: (item) => this.currencyPipe.transform(item.pricePerItem, this.po.currency) },
                { title: 'Total Price', widthPercent: 15, align: 'right',
                    calcFunc: (item): string => this.currencyPipe.transform(item.totalPrice, this.po.currency) },
            ] :
            [
                { title: 'Part Number', widthPercent: 30, align: 'left',
                    calcFunc: (item): string => item.type === PurchaseOrderTransactionPartType.Custom ? (item.vin ?? `${item.converterGroupName} Custom`)
                        : item.converterPartNumberReadable },
                { title: 'Manufacturer', widthPercent: 15, align: 'left', propertyName: 'converterGroupName' },
                { title: 'Metal Quantity', widthPercent: 16, align: 'center', propertyName: 'levelPercentageText' },
                { title: 'Quantity', widthPercent: 10, align: 'center',
                    calcFunc: (item): string => item.converterGroupName.toLocaleLowerCase() == 'loose hc' ? '0' : (item.quantity?.toString() ?? '')},
                { title: 'Price Per Item', widthPercent: 14, align: 'right',
                    calcFunc: (item): string => this.currencyPipe.transform(item.pricePerItem, this.po.currency)
                },
                { title: 'Total Price', widthPercent: 15, align: 'right',
                    calcFunc: (item): string => this.currencyPipe.transform(item.totalPrice, this.po.currency)
                },
            ];

        this.createTableHeader(columns);
        lineItems.forEach((lineItem, index) =>
        {
            if (index % 2 !== 0)
                this.drawRectanglesForTableColumns(columns, this.result.bottomOfOutput, null,
                    this.pdfDimensions.general.colors.alternateRowColor, this.pdfDimensions.general.bodyWidth, null, lineItem.customPartNumber != null);
            this.outputTableLine(lineItem, columns);
            this.checkForBottomOfPage(this.tableLineHeight);
        });
        this.createSubtotalLine(type, lineItems, columns);
        this.checkForBottomOfPage(this.tableLineHeight);
    }

    private createGroupLineItems(lines: any[], type: PurchaseOrderTransactionPartType): void
    {
        let columns = [
                { title: 'Manufacturer', widthPercent: 35, align: 'left', propertyName: 'converterGroup' },
                { title: 'Quantity', widthPercent: 15, align: 'center', propertyName: 'quantity' },
                { title: 'Price Per Item', widthPercent: 25, align: 'right',
                    calcFunc: (item): string => this.currencyPipe.transform(item.totalPrice / item.quantity, this.po.currency) },
                { title: 'Total Price', widthPercent: 25, align: 'right',
                    calcFunc: (item): string => this.currencyPipe.transform(item.totalPrice, this.po.currency) },
            ];

        if(lines.map(x => x.converterGroup.toLocaleLowerCase()).includes('loose hc')){
            columns = [
                { title: 'Manufacturer', widthPercent: 35, align: 'left', propertyName: 'converterGroup' },
                { title: 'Quantity', widthPercent: 10, align: 'center', propertyName: 'quantity' },
                { title: 'Metal Quantity', widthPercent: 15, align: 'center', 
                    calcFunc: (item): string => item.converterGroup.toLocaleLowerCase() == 'loose hc' ? item.levelPercentage + ' lbs.' : '' },
                { title: 'Price Per Item', widthPercent: 20, align: 'right',
                    calcFunc: (item): string => item.converterGroup.toLocaleLowerCase() == 'loose hc' ? this.currencyPipe.transform(item.totalPrice / item.levelPercentage, this.po.currency) : this.currencyPipe.transform(item.totalPrice / item.quantity, this.po.currency) },
                { title: 'Total Price', widthPercent: 20, align: 'right',
                    calcFunc: (item): string => this.currencyPipe.transform(item.totalPrice, this.po.currency) },
            ];
        }

        this.createTableHeader(columns);
        lines.forEach((line, index) =>
        {
            if (index % 2 !== 0)
                this.drawRectanglesForTableColumns(columns, this.result.bottomOfOutput, null,
                    this.pdfDimensions.general.colors.alternateRowColor);
            this.outputTableLine(line, columns);
            this.checkForBottomOfPage(this.tableLineHeight);
        });
        this.createSubtotalLine(type, lines, columns);
        this.checkForBottomOfPage(this.tableLineHeight);
    }
    //endregion

    //region General Table Output
    createTableHeader(columns: any, drawBorders = true, useFill = true, tableWidth = this.pdfDimensions.general.bodyWidth, fontSize: number = null): void
    {
        const general = this.pdfDimensions.general;
        const colors = general.colors;
        // Draw the header
        this.pdf.setFontSize(fontSize ?? general.regularFontSize);
        this.drawRectanglesForTableColumns(columns, this.result.bottomOfOutput, drawBorders ? this.result.borderColor : null,
            useFill ? colors.tableHeaderColor : colors.white, tableWidth, fontSize);
        let currentColumnOffset = general.marginLeft;
        const totalColumnWidthPercent = Utils.sum(columns, 'widthPercent');
        for (const col of columns)
        {
            const columnWidth = tableWidth * (col.widthPercent / totalColumnWidthPercent);
            this.outputTableValue(col.title, col, totalColumnWidthPercent, currentColumnOffset, this.result.bottomOfOutput);
            currentColumnOffset += columnWidth;
        }
        this.result.bottomOfOutput += this.tableLineHeight;
    }

    createTableRow(columns: any, rowIndex: number, drawBorders = true, useFill = true, tableWidth = this.pdfDimensions.general.bodyWidth, fontSize: number = null): void
    {
        const general = this.pdfDimensions.general;
        const colors = general.colors;
        this.pdf.setFontSize(fontSize ?? general.regularFontSize);

        let currentColumnOffset = general.marginLeft;
        const totalColumnWidthPercent = Utils.sum(columns, 'widthPercent');
        const fillColor = useFill ? colors.tableHeaderColor : colors.white;
        const drawColor = drawBorders ? this.result.borderColor : null;

        for (const col of columns)
        {
            const columnWidth = tableWidth * (col.widthPercent / totalColumnWidthPercent);
            const rowspan = col.rowspan ?? 1;

            if (rowspan > 1 && rowIndex > 0 && rowIndex < rowspan)
            {
                currentColumnOffset += columnWidth;
                continue;
            }

            const cellHeight = (rowspan > 1 && rowIndex === 0)
                ? this.tableLineHeight * rowspan
                : this.tableLineHeight;

            this.pdf.setDrawColor(drawColor == null ? fillColor : drawColor);
            this.pdf.setFillColor(fillColor);
            this.pdf.rect(currentColumnOffset, this.result.bottomOfOutput, columnWidth, cellHeight, 'DF');

            const verticalOffset = (cellHeight - this.tableLineHeight) / 2;
            this.outputTableValue(col.title, col, totalColumnWidthPercent, currentColumnOffset, this.result.bottomOfOutput + verticalOffset);

            currentColumnOffset += columnWidth;
        }

        this.result.bottomOfOutput += this.tableLineHeight;
    }

    private outputTableLine(line: any, columns: any, tableWidth = this.pdfDimensions.general.bodyWidth): void
    {
        // TODO - table line
        const general = this.pdfDimensions.general;
        let currentColumnOffset = general.marginLeft;
        const totalColumnWidthPercent = Utils.sum(columns, 'widthPercent');
        for (const col of columns)
        {
            const text = col.propertyName != null ? line[col.propertyName].toString() : col.calcFunc(line);
            const columnWidth = tableWidth * (col.widthPercent / totalColumnWidthPercent);
            if (line instanceof PurchaseOrderLineItemMiscellaneous ||
                line.type === PurchaseOrderTransactionPartType.Converter ||
                line.type === PurchaseOrderTransactionPartType.NoNumber ||
                line.type === PurchaseOrderTransactionPartType.Category ||
                (line.type === PurchaseOrderTransactionPartType.Custom && col.title !== 'Metal Quantity'))
            {
                this.outputTableValue(text, col, totalColumnWidthPercent, currentColumnOffset, this.result.bottomOfOutput);
                if(col.title.toLocaleUpperCase() == 'PART NUMBER' && line.customPartNumber)
                {
                    let currentFontSize = this.pdf.getFontSize();
                    this.pdf.setFontSize(currentFontSize - 3);
                    this.outputTableValue('Part: ' + line.customPartNumber, col, totalColumnWidthPercent, currentColumnOffset - 2, this.result.bottomOfOutput + this.tableLineHeight - 3);
                    this.pdf.setFontSize(currentFontSize);
                }
            }
            currentColumnOffset += columnWidth;
        }
        this.result.bottomOfOutput += this.tableLineHeight;
        if(line.customPartNumber)
            this.result.bottomOfOutput += this.tableLineHeight - 9;
    }

    private createSubtotalLine(type: PurchaseOrderTransactionPartType, lines: any, columns: any[]): void
    {
        const general = this.pdfDimensions.general;

        // this.result.bottomOfOutput += this.tableLineHeight / 2;
        this.checkForBottomOfPage();
        let totalPrice = Utils.sum(lines, 'totalPrice');
        if(type == PurchaseOrderTransactionPartType.Miscellaneous){
            this.drawRectanglesForTableColumns(columns, this.result.bottomOfOutput, null, general.colors.darkGray);
            this.result.bottomOfOutput += this.tableLineHeight;
            this.pdf.text('Adjustment', general.marginLeft + 2, this.result.bottomOfOutput - 4)
            let adjText = this.currencyPipe.transform(this.poManager.current.nonFerrousAdjustment, this.poManager.current.currency, true);
            this.pdf.text(adjText, general.marginLeft + general.bodyWidth - this.pdf.getTextWidth(adjText) - 2, this.result.bottomOfOutput - 4)
            totalPrice = totalPrice + this.po.nonFerrousAdjustment
        }
        if(type != PurchaseOrderTransactionPartType.Miscellaneous){
            this.drawRectanglesForTableColumns(columns, this.result.bottomOfOutput, null, general.colors.darkGray);
            this.result.bottomOfOutput += this.tableLineHeight;
            this.pdf.text('Adjustment', general.marginLeft + 2, this.result.bottomOfOutput - 4)
            let adjText = this.currencyPipe.transform(this.poManager.current.catsAdjustment, this.poManager.current.currency, true);
            this.pdf.text(adjText, general.marginLeft + general.bodyWidth - this.pdf.getTextWidth(adjText) - 2, this.result.bottomOfOutput - 4)
            totalPrice = totalPrice + this.po.catsAdjustment
        }
        this.drawRectanglesForTableColumns(columns, this.result.bottomOfOutput, 'black', general.colors.darkGray);
        let currentColumnOffset = general.marginLeft;
        
        let quantity = 0
        let metalQuantity = null;
        if(lines.every(x => x instanceof PurchaseOrderLineItem)) {
            metalQuantity = Utils.sum(lines.filter(x => x.converterGroupName.toLocaleLowerCase() == 'loose hc'), 'levelPercentage')
            quantity = Utils.sum(lines.filter(x => x.converterGroupName.toLocaleLowerCase() != 'loose hc'), 'quantity')
        } else if(!lines.every(x => x instanceof PurchaseOrderLineItemMiscellaneous)) {
            metalQuantity = Utils.sum(lines.filter(x => x.converterGroup.toLocaleLowerCase() == 'loose hc'), 'levelPercentage')
            quantity = Utils.sum(lines.filter(x => x.converterGroup.toLocaleLowerCase() != 'loose hc'), 'quantity')
        } else {
            quantity = Utils.sum(lines, 'quantity')
        }
        
        if(metalQuantity == 0)
            metalQuantity = null;
        else
            metalQuantity = metalQuantity + ' lbs.'
        const totalColumnWidthPercent = Utils.sum(columns, 'widthPercent');
        const decimals = true;
        for (let iColumn = 0; iColumn < columns.length; iColumn++)
        {
            const col = columns[iColumn];
            const text = iColumn === 0 ? PurchaseOrderPDFComponent.tableTitle(type) + ' SUBTOTAL' :
                col.title === 'Quantity' ? quantity.toString() :
                    col.title === 'Price Per Item' ? (quantity !== 0 ? this.currencyPipe.transform(totalPrice / quantity, this.po.currency, decimals) : null) :
                        col.title === 'Total Price' ? this.currencyPipe.transform(totalPrice, this.po.currency, true, false) :
                            col.title === 'Metal Quantity' ? metalQuantity : null;

            const columnWidth = general.bodyWidth * (col.widthPercent / totalColumnWidthPercent);
            if (text != null)
                this.outputTableValue(text, col, totalColumnWidthPercent, currentColumnOffset, this.result.bottomOfOutput);
            currentColumnOffset += columnWidth;
        }
    }

    drawRectanglesForTableColumns(columns: any, top: number, drawColor = 'black', fillColor = 'white',
                                          tableWidth = this.pdfDimensions.general.bodyWidth, fontSize: number = null, isCustomPartnumber: boolean = false): void
    {
        // TODO - rysowanie tabeli
        this.pdf.setDrawColor(drawColor == null ? fillColor : drawColor);
        this.pdf.setFillColor(fillColor);
        this.pdf.setFontSize(fontSize ?? this.pdfDimensions.general.regularFontSize);
        let currentColumnOffset = this.pdfDimensions.general.marginLeft;
        const totalColumnWidthPercent = Utils.sum(columns, 'widthPercent');
        for (const col of columns)
        {
            const columnWidth = tableWidth * (col.widthPercent / totalColumnWidthPercent);
            this.pdf.rect(currentColumnOffset, top, columnWidth, isCustomPartnumber ? this.tableLineHeight + 6 : this.tableLineHeight, 'DF');
            currentColumnOffset += columnWidth;
        }
    }

    outputTableValue(text: string, column: any, totalColumnWidthPercent: number, left: number, top: number): void
    {
        const general = this.pdfDimensions.general;
        const columnWidth = general.bodyWidth * (column.widthPercent / totalColumnWidthPercent);
        let truncatedText = text;
        if (this.textWidth(truncatedText) > columnWidth) {
            // Truncate the text
            const ellipsisWidth = this.textWidth('...');
            while (this.textWidth(truncatedText) + ellipsisWidth > columnWidth)
            {
                truncatedText = truncatedText.substring(0, truncatedText.length - 1);
            }
            truncatedText += '...';
        }
        const truncatedWidth = this.textWidth(truncatedText);
        const textLeft = column.align === 'left' ? left + 3 :
            column.align === 'right' ? left + columnWidth - truncatedWidth - general.textPadding :
                left + columnWidth / 2 - truncatedWidth / 2;
        this.pdf.text(truncatedText, textLeft, top + this.lineHeight);
    }

    private addSpaceBetweenTables(): void
    {
        if (!this.checkForBottomOfPage(this.tableLineHeight * 3))
            this.result.bottomOfOutput += this.tableLineHeight * 3;
    }
    //endregion

    //region Compliance pages
    async outputQuote(showDetails: boolean): Promise<void> {
        this.pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'px',
            format: 'letter'
        });
        this.pdfDimensions.general.bodyWidth = this.pdf.internal.pageSize.width - this.pdfDimensions.general.marginLeft * 2;
        this.pdfDimensions.general.pageHeight = this.pdf.internal.pageSize.height;
        
        this.result = new PdfSectionOutputResult();
        this.showDetails = showDetails;
        const pdfPages = [ ];
        this.po = this.poManager.current;
        this.outputPageFooter();
        let orgDate = this.poManager.current.purchaseDate;
        this.poManager.current.purchaseDate = new Date();
        this.createHeader();
        this.poManager.current.purchaseDate = orgDate;
        const willOutputConverters = this.po.lineItemsAllConverters.length > 0;
        const willOutputMisc = this.po.lineItemsMiscellaneous.length > 0;
        this.createConvertersTable();
        if (willOutputConverters && willOutputMisc) 
            this.addSpaceBetweenTables();
        this.createMiscTable();
        this.createNotes();
        pdfPages.push(this.pdf);

        let orgColor = this.pdf.getTextColor();
        let orgFontSize = this.pdf.getFontSize();
        let orgFont = this.pdf.getFont();

        this.pdf.setTextColor(0, 0, 0);
        this.pdf.setFont(orgFont.fontName, orgFont.fontStyle, 'bold')
        this.pdf.setFontSize(150)

        for(let i=0; i< this.pdf.getNumberOfPages(); i++){
            this.pdf.setPage(i);
            this.pdf.saveGraphicsState();
            this.pdf.setGState(this.pdf.GState({opacity: 0.2}));
            this.pdf.text("QUOTE", (this.pdfDimensions.general.bodyWidth / 2) - 55, (this.pdfDimensions.general.pageHeight / 2) + 170, { angle: 55})
            this.pdf.restoreGraphicsState();
        }

        this.pdf.setPage(1)
        this.pdf.setFontSize(10)
        this.pdf.setTextColor('red');
        this.pdf.setFont(orgFont.fontName, orgFont.fontStyle, 'normal')
        let text = '*Prices are subject to change due to market fluctuation.'
        let left = this.pdfDimensions.general.bodyWidth + this.pdfDimensions.general.marginLeft - this.pdf.getTextWidth(text);
        this.pdf.text(text, left, this.lineHeight + 4)

        this.pdf.setTextColor(orgColor);
        this.pdf.setFontSize(orgFontSize);
        await this.openOrEmailPdf(pdfPages, false);
    }

    isAdminEdit: boolean = false;
    captureSignature(signaturePageType: SignaturePageType, adminEdit: boolean = false): void
    {
        this.po = this.poManager.current;
        this.isAdminEdit = adminEdit;
        if(!adminEdit){
            if(this.po.signaturePageUrl == null && this.po.isLocked){
                this.signatureCompleted.emit(true);
                return;
            }
            // Simply fire event that signature was completed if it does not require one or already has one
            if (this.po.signaturePageUrl != null || this.po.location == null ||
                this.authManager.currentUser.isAssayUser || this.authManager.currentUser.exemptFromVendorLicenseRequirements)
            {
                this.signatureCompleted.emit(true);
                return;
            }
        }
        
        this.needsSignature = true;
        this.signaturePageType = signaturePageType;
        if(this.signaturePageType == SignaturePageType.California)
            this.caCompliancePage?.ngOnInit();
        else if(this.signaturePageType == SignaturePageType.Illinois)
            this.ilCompliancePage?.ngOnInit();
        else if(this.signaturePageType == SignaturePageType.Missisipi)
            this.msCompliancePage?.ngOnInit();
        else if(this.signaturePageType == SignaturePageType.Texas)
            this.txCompliancePage?.ngOnInit();
        else if(this.signaturePageType == SignaturePageType.WestVirginia)
            this.wvCompliancePage?.ngOnInit();
        else
            this.defaultCompliancePage?.ngOnInit();
    }

    onSignatureCompleted(completed: boolean): void
    {
        this.signatureCompleted.emit(completed);
    }

    onSignaturePdfGenerated(completed: boolean): void
    {
        // this.signatureCompleted.emit(completed);
    }

    // get hasCompliancePage(): boolean { return this.poManager.current != null && this.poManager.current.location != null; }
    //
    // get hasDefaultCompliancePage(): boolean {
    //     const state = this.poManager.current.location.state.toLowerCase();
    //     return this.hasCompliancePage && state !== 'ca' && state !== 'il' && state !== 'tx';
    // }

    /* tslint:disable:max-line-length */
    private createTexasCompliancePage(signatureImage: any): void
    {
        this.createNewPdfPage(false);
        this.outputComplianceHeader();

        // PO Information
        // Use "table headers" to create the pseudo-table
        this.result.bottomOfOutput -= this.lineHeight * 0.5;
        const columns = [
            { title: 'PO #', widthPercent: 50, align: 'left' },
            { title: 'Total Purchase', widthPercent: 50, align: 'left' },
        ];
        this.createTableHeader(columns, true, false, this.pdfDimensions.general.bodyWidth * 0.5);
        columns[0].title = this.po.number;
        columns[1].title = this.currencyPipe.transform(this.po.totalConvertersPrice, this.po.currency);
        this.createTableHeader(columns, true, false, this.pdfDimensions.general.bodyWidth * 0.5);
        this.pdf.setFontSize(12);

        // Buyer
        this.result.bottomOfOutput += this.lineHeight * 3;
        this.outputTextLine('Buyer:');
        this.result.bottomOfOutput += this.lineHeight;
        this.outputTextLine(this.po.buyerName);
        this.outputTextLine(this.po.location.address);
        this.outputTextLine(`${this.po.location.city}, ${this.po.location.state}  ${this.po.location.zip}`);

        // Text
        this.result.bottomOfOutput += this.lineHeight * 2;
        this.outputTextLine('I, the undersigned, hereby certify that I am the legal owner of all the merchandise sold. I have');
        this.outputTextLine('an active license, certificate, registration, documentation showing my business entity status at');
        this.outputTextLine('point of sale. I sell regulated material and/or regulated metal in the ordinary course of my');
        this.outputTextLine('business and I acknowledge this receipt as payment in full.');

        // Seller
        this.result.bottomOfOutput += this.lineHeight * 2;
        this.outputTextLine('Seller:');
        this.result.bottomOfOutput += this.lineHeight;
        this.outputTextLine(this.po.vendor.name);
        this.outputTextLine(this.po.vendor.address);
        this.outputTextLine(`${this.po.vendor.city}, ${this.po.vendor.state}  ${this.po.vendor.zip}`);

        // Signature block
        this.result.bottomOfOutput += this.lineHeight * 3;
        this.outputTextLine('Signature ________________________________________            Date ___________________ ');
        this.result.bottomOfOutput += this.lineHeight * 2;
        this.outputTextLine('Print ____________________________________________ ');
    }

    outputComplianceHeader(text: string = 'Purchase Agreement'): void
    {
        this.pdf.setFont('times');
        this.pdf.setFontSize(20);
        this.pdf.text(text, this.center - this.textWidth(text) / 2, this.result.bottomOfOutput);
        this.result.bottomOfOutput += this.lineHeight * 1.5;
    }
    /* tslint:enable:max-line-length */
    //endregion

    //region Misc.
    private createTableTitle(type: PurchaseOrderTransactionPartType): void
    {
        if (this.result.bottomOfOutput > this.bottomOfOutputArea) this.createNewPdfPage();

        this.pdf.setFontSize(this.pdfDimensions.general.tableHeaderSize);
        this.pdf.setDrawColor(0, 0, 0);
        this.pdf.text(PurchaseOrderPDFComponent.tableTitle(type), this.pdfDimensions.general.marginLeft, this.result.bottomOfOutput);
        this.result.bottomOfOutput += 5;
    }

    // tslint:disable-next-line:member-ordering
    private static tableTitle(type: PurchaseOrderTransactionPartType): string
    {
        switch (type)
        {
            case PurchaseOrderTransactionPartType.Converter: return 'Converters';
            case PurchaseOrderTransactionPartType.Custom: return 'Custom Converters';
            case PurchaseOrderTransactionPartType.NoNumber: return 'No-Number Converters';
            case PurchaseOrderTransactionPartType.Category: return 'Category Converters';
            case PurchaseOrderTransactionPartType.Miscellaneous: return 'Non-Ferrous';
        }
    }

    checkForBottomOfPage(lineHeight = 0, includeFooter: boolean = true): boolean
    {
        const needsPageBreak = this.result.bottomOfOutput + lineHeight >= this.bottomOfOutputArea;
        if (needsPageBreak)
            this.createNewPdfPage(includeFooter);
        return needsPageBreak;
    }

    createNewPdfPage(includeFooter = true): void
    {
        this.pdf.addPage();

        this.result.bottomOfOutput = this.pdfDimensions.general.marginTop;
        this.result.currentPageNumber++;
        if (includeFooter) this.outputPageFooter();
    }

    outputText(text: string, indent = 0, options: TextOptionsLight = null): void
    {
        this.pdf.text(text ?? '', this.pdfDimensions.general.marginLeft + indent, this.result.bottomOfOutput, options);
    }

    outputTextLine(text: string, indent = 0, options: TextOptionsLight = null): void
    {
        this.pdf.text(text ?? '', this.pdfDimensions.general.marginLeft + indent, this.result.bottomOfOutput, options);
        this.result.bottomOfOutput += this.lineHeight;
    }

    outputTextLines(lines: string[], indent = 0, hangingIndent = 0): void
    {
        if (hangingIndent === 0)
        {
            this.pdf.text(lines, this.pdfDimensions.general.marginLeft + indent, this.result.bottomOfOutput);
            this.result.bottomOfOutput += this.lineHeight * lines.length;
        }
        else
        {
            this.pdf.text(lines[0], this.pdfDimensions.general.marginLeft + indent, this.result.bottomOfOutput);
            this.result.bottomOfOutput += this.lineHeight;
            this.pdf.text(lines.slice(1), this.pdfDimensions.general.marginLeft + hangingIndent, this.result.bottomOfOutput);
            this.result.bottomOfOutput += this.lineHeight * (lines.length - 1);
        }
    }

    outputPageFooter(): void
    {
        this.pdf.setFontSize(this.pdfDimensions.general.regularFontSize);
        const pageText = `Page ${this.result.currentPageNumber}`;
        this.pdf.text(pageText, this.pdfDimensions.general.marginLeft + this.pdfDimensions.general.bodyWidth -
            this.textWidth(pageText), this.pdfDimensions.general.pageHeight - 15);
    }

    outputCheckbox(left: number, checked: boolean): void
    {
        this.pdf.rect(this.leftMargin + left, this.result.bottomOfOutput - 8, 10, 10);
        if (checked)
        {
            this.pdf.line(this.leftMargin + left, this.result.bottomOfOutput - 8,
                this.leftMargin + left + 10, this.result.bottomOfOutput + 2);
            this.pdf.line(this.leftMargin + left + 10, this.result.bottomOfOutput - 8,
                this.leftMargin + left, this.result.bottomOfOutput + 2);
        }
    }

    outputImage(image: any, type: string, left: number, width: number, height: number): void
    {
        if (image == null) return;
        this.pdf.addImage(image, type, left, this.result.bottomOfOutput - height, width, height);
    }

    get bottomOfOutputArea(): number
    {
        return this.pdfDimensions.general.pageHeight - this.lineHeight * 2.5;
    }

    textWidth(text: string): number
    {
        return this.pdf.getTextWidth(text);
    }

    get top(): number { return this.pdfDimensions.general.marginTop; }
    get leftMargin(): number { return this.pdfDimensions.general.marginLeft; }
    get center(): number { return this.pdfDimensions.general.marginLeft + this.pdfDimensions.general.bodyWidth / 2; }
    get rightMargin(): number { return this.pdfDimensions.general.marginLeft + this.pdfDimensions.general.bodyWidth; }
    get width(): number { return this.pdfDimensions.general.bodyWidth; }
    get tableLineHeight(): number { return this.pdf.getFontSize() + 4; }
    get lineHeight(): number { return this.pdf.getFontSize(); }

    // tslint:disable-next-line:member-ordering
    static removeUnderscores(text: string): string
    {
        return text.replace(/_/g, '');
    }
    //endregion
}
